import { emptyArrayOf } from 'ui-components/default-props/empty';

import { PARCEL_ONLY_RATE_ERROR_IDS } from '../user-friendly-error-message-service/message-props-builders/property-cannot-be-null-or-empty';

// Matches raw backend FluentValidation text for fields that only apply to parcel shipments.
// E.g. "'Package Type Id' should not be empty." or "'Packages[0].Weight' must not be empty."
// ShipTo.* fields are intentionally absent — they apply to freight shipments too.
// The Packages alternative uses [^']+ to capture indexed paths like "Packages[1].PackageTypeId".
const PARCEL_FIELD_RAW_TEXT_PATTERN =
  /'(Package Type Id|PackageTypeId|Packages[^']+|Weight|Length|Width|Height|Service Id|ServiceId|Seller Label Provider Id|SellerLabelProviderId)'/i;

const FALLTHROUGH_DESCRIPTOR_ID = 'code-to-formatted-message-props.originalMessage';

export type RateErrorEntry = Record<string, unknown> | string;

const getDefaultMessage = (entry: RateErrorEntry): string | null => {
  if (typeof entry === 'string') return entry;
  if (typeof entry === 'object' && entry !== null && typeof entry.defaultMessage === 'string') {
    return entry.defaultMessage;
  }
  return null;
};

// ShipTo.* errors are intentionally excluded — freight plans share destination address requirements.
export const isParcelOnlyRateError = (entry: RateErrorEntry): boolean => {
  if (typeof entry === 'object' && entry !== null) {
    const id = entry.id as string | undefined;

    if (id && PARCEL_ONLY_RATE_ERROR_IDS.includes(id)) {
      return true;
    }

    if (id === FALLTHROUGH_DESCRIPTOR_ID) {
      const raw = getDefaultMessage(entry);
      return raw !== null && PARCEL_FIELD_RAW_TEXT_PATTERN.test(raw);
    }

    return false;
  }

  if (typeof entry === 'string') {
    return PARCEL_FIELD_RAW_TEXT_PATTERN.test(entry);
  }

  return false;
};

export const filterOutParcelOnlyRateErrors = (errors: RateErrorEntry[]): RateErrorEntry[] => {
  if (!errors?.length) return emptyArrayOf<RateErrorEntry>();
  const filtered = errors.filter((e) => !isParcelOnlyRateError(e));
  if (filtered.length === errors.length) return errors;
  if (filtered.length === 0) return emptyArrayOf<RateErrorEntry>();
  return filtered;
};
